import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Grid, Typography, Button, Box, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import TripCard from '../components/TripCard';
import { getTrips, joinTrip } from '../services/api';
import { useAuth } from '../App';

export function getTripStatus(startDate, endDate) {
  if (!startDate && !endDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (startDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    if (start > today) return { label: 'Upcoming', color: 'info' };
  }
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    if (end < today) return { label: 'Completed', color: 'default' };
  }
  // startDate exists and is <= today, and endDate is either missing or >= today
  if (startDate) return { label: 'In Progress', color: 'success' };
  return null;
}

export default function Home() {
  const { setUser } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joinOpen, setJoinOpen] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loadTrips = async () => {
      try {
        const { data } = await getTrips();
        setTrips([...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } catch (err) {
        if (err.response?.status === 401) {
          setUser(null);
        } else {
          setError('Failed to load trips. Please refresh.');
        }
      } finally {
        setLoading(false);
      }
    };
    loadTrips();
  }, [setUser]);

  const filteredTrips = useMemo(() => {
    if (!search.trim()) return trips;
    const q = search.toLowerCase();
    return trips.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q),
    );
  }, [trips, search]);

  const handleJoin = async () => {
    setJoinError('');
    try {
      const { data } = await joinTrip(shareCode.trim().toUpperCase());
      setTrips((prev) => [...prev.filter((t) => t.tripId !== data.tripId), data]);
      setJoinOpen(false);
      setShareCode('');
    } catch {
      setJoinError('Invalid share code. Please try again.');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">My Trips</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => setJoinOpen(true)}>
            Join Trip
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/trips/new')}>
            New Trip
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {trips.length > 0 && (
        <TextField
          fullWidth
          size="small"
          placeholder="Search trips by name or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 3 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
      )}

      {trips.length === 0 ? (
        <Box textAlign="center" mt={10}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No trips yet</Typography>
          <Typography color="text.secondary" mb={3}>
            Create your first trip or join one with a share code
          </Typography>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => navigate('/trips/new')}>
            Create First Trip
          </Button>
        </Box>
      ) : filteredTrips.length === 0 ? (
        <Box textAlign="center" mt={6}>
          <Typography color="text.secondary">No trips match your search.</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTrips.map((trip) => (
            <Grid item xs={12} sm={6} md={4} key={trip.tripId}>
              <TripCard
                trip={trip}
                status={getTripStatus(trip.startDate, trip.endDate)}
                onClick={() => navigate(`/trips/${trip.tripId}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={joinOpen} onClose={() => setJoinOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Join a Trip</DialogTitle>
        <DialogContent>
          {joinError && <Alert severity="error" sx={{ mb: 2 }}>{joinError}</Alert>}
          <TextField
            autoFocus fullWidth label="Share Code" value={shareCode}
            onChange={(e) => setShareCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
            sx={{ mt: 1 }}
            inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 4, fontSize: 20 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleJoin} disabled={!shareCode.trim()}>Join</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
